import React, { useState, useCallback, useEffect } from 'react';
import useGameStore from '../store/gameStore';
import useAudioStore from '../store/audioStore';
import { useTranslation } from '../i18n';
import { isWorkingDay } from '../utils/format';
import { InteractiveMap } from '../components/game/InteractiveMap';
import { Activity } from 'lucide-react';

import { KpiStrip } from '../components/dashboard/KpiStrip';
import { DayControls } from '../components/dashboard/DayControls';
import { TradeshowBanner } from '../components/dashboard/TradeshowBanner';
import { MonthlyKpis } from '../components/dashboard/MonthlyKpis';
import { AlertsList } from '../components/dashboard/AlertsList';
import { TreasuryChart } from '../components/dashboard/TreasuryChart';
import ObjectivesWidget from '../components/dashboard/ObjectivesWidget';
import MarketingSalonWidget from '../components/dashboard/MarketingSalonWidget';
import BenefitsCard from '../components/dashboard/BenefitsCard';
import UrgentMessagesBanner from '../components/dashboard/UrgentMessagesBanner';
import RetailKpiWidget from '../components/dashboard/RetailKpiWidget';
import TraficDecompoCard from '../components/dashboard/TraficDecompoCard';
import InvestissementsCard from '../components/dashboard/InvestissementsCard';
import ReseauCard from '../components/dashboard/ReseauCard';
import ScenarioBanner from '../components/game/ScenarioBanner';

// SCD Lot 0 — Dashboard allégé : les widgets flotte / contrats B2B /
// appels d'offres du fork SCD ont été retirés. La boucle retail
// (trafic × conversion × panier moyen) sera branchée dans les lots suivants.
export default function Dashboard() {
  const {
    company, day, month, year, totalDays, treasury, phase, reputation, notoriety,
    difficulty,
    vehicles, drivers, activeContracts,
    pendingEvents, lastDayRevenue, lastDayExpenses, monthlyRevenues, monthlyExpenses,
    treasuryHistory, alerts,
    nextDay, hasSupportUnlock, attendTradeShow, tradeShowMonth,
  } = useGameStore();

  const playSound = useAudioStore((s) => s.play);
  const { t } = useTranslation();

  const [fastForwarding, setFastForwarding] = useState(false);

  const working = isWorkingDay(day);
  const hasPending = pendingEvents.some(e => !e.scheduledFor || e.scheduledFor <= totalDays);
  const dailyDelta = lastDayRevenue - lastDayExpenses;

  const handleNextDay = () => {
    playSound('nextDay');
    nextDay();
  };

  const handleFastForward = useCallback(async (numDays) => {
    playSound('fastForward');
    setFastForwarding(true);
    for (let i = 0; i < numDays; i++) {
      const state = useGameStore.getState();
      const tDays = state.totalDays || 0;
      const hasResolvable = (state.pendingEvents || []).some(
        e => !e.scheduledFor || e.scheduledFor <= tDays
      );
      if (hasResolvable) break;
      const success = state.nextDay();
      if (!success) break;
    }
    setFastForwarding(false);
  }, [playSound, setFastForwarding]);

  // Raccourci clavier BARRE ESPACE = passer un jour.
  useEffect(() => {
    function onKey(e) {
      if (e.code !== 'Space' && e.key !== ' ') return;
      const target = e.target;
      if (!target) return;
      const tag = (target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable) return;
      if (document.querySelector('[role="dialog"], .ReactModal__Overlay--after-open')) return;
      const state = useGameStore.getState();
      const tDays = state.totalDays || 0;
      const blocked = (state.pendingEvents || []).some(ev => !ev.scheduledFor || ev.scheduledFor <= tDays);
      if (blocked) return;
      e.preventDefault();
      playSound('nextDay');
      state.nextDay();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playSound]);

  const chartData = treasuryHistory.slice(-30).map((h, i) => ({
    name: `J${i + 1}`,
    trésorerie: Math.round(h.treasury),
    revenus: h.revenue,
    charges: h.expenses,
  }));

  return (
    <div className="space-y-5" data-testid="dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline">{t('pilot_console')} · J{totalDays}</p>
          <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-foreground mt-0.5">
            {t('dashboard_title')}
          </h1>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-400">
          <Activity className="w-3.5 h-3.5 text-accent animate-pulse-glow" />
          <span>{t('system_operational')}</span>
          {difficulty && (
            <span
              className={`text-[9px] px-2 py-0.5 rounded-sm font-semibold tracking-wider uppercase ${
                difficulty === 'facile'
                  ? 'bg-money/15 text-money border border-money/30'
                  : difficulty === 'difficile'
                  ? 'bg-danger/15 text-danger border border-danger/30'
                  : 'bg-accent/15 text-accent border border-accent/30'
              }`}
              data-testid="difficulty-badge"
            >
              {difficulty === 'facile' ? 'EASY' : difficulty === 'difficile' ? 'HARD' : 'NORM'}
            </span>
          )}
        </div>
      </div>

      <KpiStrip
        day={day}
        month={month}
        year={year}
        working={working}
        phase={phase}
        treasury={treasury}
        dailyDelta={dailyDelta}
        reputation={reputation}
        notoriety={notoriety}
      />

      <DayControls
        hasPending={hasPending}
        fastForwarding={fastForwarding}
        onNextDay={handleNextDay}
        onFastForward={handleFastForward}
      />

      <UrgentMessagesBanner />

      {/* SCD Lot 1 — KPI retail : CA, marge, rotation, ruptures, démarque. */}
      <RetailKpiWidget />
      <ScenarioBanner />
      <TraficDecompoCard compact />

      <InvestissementsCard />

      <ReseauCard />

      <ObjectivesWidget />

      <MarketingSalonWidget />

      {typeof hasSupportUnlock === 'function' && hasSupportUnlock('salons') && (
        <TradeshowBanner
          month={month}
          tradeShowMonth={tradeShowMonth}
          treasury={treasury}
          onAttend={attendTradeShow}
        />
      )}

      <MonthlyKpis monthlyRevenues={monthlyRevenues} monthlyExpenses={monthlyExpenses} />

      <BenefitsCard />

      <AlertsList alerts={alerts} />

      <TreasuryChart chartData={chartData} />

      <div className="surface-card overflow-hidden">
        <div className="px-4 py-3 border-b border-ink-600 flex items-center justify-between">
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">
            Carte stratégique
          </h3>
          <span className="text-[10px] text-slate-500 font-mono">Phase {phase}</span>
        </div>
        <div className="h-[460px] relative">
          <InteractiveMap
            hqCityId={company?.city || 'paris'}
            phase={phase}
            activeContracts={activeContracts}
            vehicles={vehicles}
            drivers={drivers}
          />
        </div>
      </div>
    </div>
  );
}
